/*
 * Copyright (c) Worldline 2023.
 */

import {NgModule} from '@angular/core';
import {DefaultUrlSerializer, RouterModule, Routes, UrlSerializer, UrlTree} from '@angular/router';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';

import {SignaturesComponent} from "./components/signatures/signatures.component";
import {EvidencesComponent} from "./components/evidences/evidences.component";
import {AgreementsComponent} from "./components/agreements/agreements.component";
import {SmartphoneAgreementComponent} from "./components/smartphone-agreement/smartphone-agreement.component";
import {ConfirmCancellationComponent} from "./components/confirm-cancellation/confirm-cancellation.component";
import {CancellationComponent} from "./components/cancellation/cancellation.component";
import {UnexpectedErrorComponent} from "./components/unexpected-error/unexpected-error.component";
import {Page404Component} from "./components/page404/page404.component";
import {OnHoldComponent} from "./components/on-hold/on-hold.component";
import {ErrorComponent} from "./components/error/error.component";
import {HelpComponent} from "./components/help/help.component";
import {ContactComponent} from "./components/contact/contact.component";
import {CustomizationComponent} from "./components/customization/customization.component";
import {LegalNoticesComponent} from "./components/legal-notices/legal-notices.component";
import {SessionComponent} from "./components/session/session.component";
import {PendingSignatureComponent} from "./components/pending-signature/pending-signature.component";
import {Routes as SbxRoutes} from './constant/routes';
import {ResumeComponent} from "./components/resume/resume.component";
import {TokenRenewalComponent} from "./components/token-renewal/token-renewal.component";
import {RedirectComponent} from "./components/redirect/redirect.component";

const routes: Routes = [
    {
        path: SbxRoutes.session, children: [
            {path: 'pId/:pId', children: [{path: '**', component: SessionComponent}]},
            {path: '**', component: SessionComponent}
        ]
    },
    {
        path: SbxRoutes.pendingSignature, children: [
            {path: 'pId/:pId', children: [{path: '**', component: PendingSignatureComponent}]},
            {path: '**', component: PendingSignatureComponent}
        ]
    },
    {
        path: SbxRoutes.signatures, children: [
            {path: 'pId/:pId', children: [{path: '**', component: SignaturesComponent}]},
            {path: '**', component: SignaturesComponent}
        ]
    },
    {
        path: SbxRoutes.agreements, children: [
            {path: 'pId/:pId', children: [{path: '**', component: AgreementsComponent}]},
            {path: '**', component: AgreementsComponent}]
    },
    {
        path: SbxRoutes.evidences, children: [
            {path: 'pId/:pId', children: [{path: '**', component: EvidencesComponent}]},
            {path: '**', component: EvidencesComponent}]
    },
    {
        path: SbxRoutes.confirmCancellation, children: [
            {path: 'pId/:pId', children: [{path: '**', component: ConfirmCancellationComponent}]},
            {path: '**', component: ConfirmCancellationComponent}]
    },
    {
        path: SbxRoutes.canceled, children: [
            {path: 'pId/:pId', children: [{path: '**', component: CancellationComponent}]},
            {path: '**', component: CancellationComponent}]
    },
    {
        path: SbxRoutes.unexpectedError, children: [
            {path: 'pId/:pId', children: [{path: '**', component: UnexpectedErrorComponent}]},
            {path: '**', component: UnexpectedErrorComponent}]
    },
    {
        path: SbxRoutes.unexpectedErrorWithoutLocale, children: [
            {path: 'pId/:pId', children: [{path: '**', component: UnexpectedErrorComponent}]},
            {path: '**', component: UnexpectedErrorComponent}]
    },
    {
        path: SbxRoutes.page404, children: [
            {path: 'pId/:pId', children: [{path: '**', component: Page404Component}]},
            {path: '**', component: Page404Component}]
    },
    {
        path: SbxRoutes.tokenRenewal, children: [
            {path: 'pId/:pId', children: [{path: '**', component: TokenRenewalComponent}]},
            {path: '**', component: TokenRenewalComponent}]
    },
    {
        path: SbxRoutes.hold, children: [
            {path: 'pId/:pId', children: [{path: '**', component: OnHoldComponent}]},
            {path: '**', component: OnHoldComponent}]
    },
    {
        path: SbxRoutes.error, children: [
            {path: 'pId/:pId', children: [{path: '**', component: ErrorComponent}]},
            {path: '**', component: ErrorComponent}]
    },
    {
        path: SbxRoutes.help, children: [
            {path: 'pId/:pId', children: [{path: '**', component: HelpComponent}]},
            {path: '**', component: HelpComponent}]
    },
    {
        path: SbxRoutes.contact, children: [
            {path: 'pId/:pId', children: [{path: '**', component: ContactComponent}]},
            {path: '**', component: ContactComponent}]
    },
    {
        path: SbxRoutes.customization, children: [
            {path: '**', component: CustomizationComponent}]
    },
    {
        path: SbxRoutes.legalNotices, children: [
            {path: 'pId/:pId', children: [{path: '**', component: LegalNoticesComponent}]},
            {path: '**', component: LegalNoticesComponent}]
    },
    {
        path: SbxRoutes.smartphoneagreements, children: [
            {path: 'pId/:pId', children: [{path: '**', component: SmartphoneAgreementComponent}]},
            {path: '**', component: SmartphoneAgreementComponent}]
    },
    {
        path: SbxRoutes.resume, children: [
            {path: 'pId/:pId', children: [{path: '**', component: ResumeComponent}]},
            {path: '**', component: ResumeComponent}]
    },
    {
        path: SbxRoutes.redirection, children: [
            {path: 'pId/:pId', children: [{path: '**', component: RedirectComponent}]},
            {path: '**', component: RedirectComponent}]
    },
    {path: '**', pathMatch: 'full', component: Page404Component},
];

export default class CustomUrlSerializer implements UrlSerializer {
    private _defaultUrlSerializer: DefaultUrlSerializer = new DefaultUrlSerializer();

    parse(url: string): UrlTree {
        // Encode parentheses
        url = url.replace(/\(/g, '%28').replace(/\)/g, '%29');
        // Use the default serializer.
        return this._defaultUrlSerializer.parse(url)
    }

    serialize(tree: UrlTree): string {
        return this._defaultUrlSerializer.serialize(tree).replace(/%28/g, '(').replace(/%29/g, ')');
    }
}


@NgModule({
    imports: [RouterModule.forRoot(routes, {onSameUrlNavigation: 'reload'})],
    exports: [RouterModule],
    providers: [
        {provide: LocationStrategy, useClass: HashLocationStrategy},
        // {provide: RouteReuseStrategy, useClass: CustomReuseStrategy},
        {provide: UrlSerializer, useClass: CustomUrlSerializer},
    ]
})
export class AppRoutingModule {

    constructor() {
    }
}
